import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { logger } from "../lib/logger";
import { Bell, Calendar, Clock, ArrowLeft, Video, ExternalLink, NotebookPen, MessageSquare, ShieldCheck, MapPin, RefreshCcw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import DailyIframe from "@daily-co/daily-js";
import PresenceDot from "../components/PresenceDot";
import WaitingRoom from "../components/WaitingRoom";
import PreCallCheck from "../components/PreCallCheck";
import { useChat } from "../contexts/ChatContext";

function fmtFull(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function PersonCard({ label, person, testid, showPresence, presenceRole = "counsellor" }) {
  return (
    <div className="brand-card p-6" data-testid={testid}>
      <div className="overline mb-3">{label}</div>
      {person ? (
        <>
          <div className="flex items-start gap-3">
            {person.picture ? (
              <img src={person.picture} className="w-14 h-14 rounded-2xl object-cover" alt="" />
            ) : (
              <div className="w-14 h-14 rounded-2xl bg-accent flex items-center justify-center font-extrabold text-[#3D405B]">{person.name?.[0] || "?"}</div>
            )}
            <div className="min-w-0 flex-1">
              <div className="font-extrabold text-[#3D405B] truncate">{person.name}</div>
              {showPresence && person.user_id && (
                <div className="mt-1"><PresenceDot userId={person.user_id} role={presenceRole} /></div>
              )}
              {person.email && (
                <a href={`mailto:${person.email}`} className="text-xs text-[#7A7D96] hover:text-primary truncate block mt-1">{person.email}</a>
              )}
              {person.phone && (
                <a href={`tel:${person.phone}`} className="text-xs text-[#7A7D96] hover:text-primary block">{person.phone}</a>
              )}
            </div>
          </div>
          <Link to={`/chat?with=${person.user_id}`} className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#3D405B]/5 text-[#3D405B] text-sm font-bold hover:bg-[#3D405B]/10 transition-all" data-testid={`${testid}-chat`}>
            <MessageSquare size={15} /> Open chat
          </Link>
        </>
      ) : (
        <div className="text-sm text-[#7A7D96] italic">not available</div>
      )}
    </div>
  );
}

export default function SessionDetail() {
  const { bookingId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joined, setJoined] = useState(false);
  const [waitingForCounsellor, setWaitingForCounsellor] = useState(false);
  const [waitingSecs, setWaitingSecs] = useState(0);
  const [starting, setStarting] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());

  // Tick every 30s so the countdown re-renders and the join button unlocks automatically
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);
  const [notesPrivate, setNotesPrivate] = useState("");
  const [summaryClient, setSummaryClient] = useState("");
  const [switching, setSwitching] = useState(false);
  const [completing, setCompleting] = useState(false);

  // Daily.co embed refs (declared here to keep hook order stable regardless of early returns)
  const dailyRef = useRef(null);
  const dailyFrameRef = useRef(null);
  const [pinging, setPinging] = useState(false);
  const [lastPingedAt, setLastPingedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/bookings/${bookingId}`);
      setBooking(data);
      setNotesPrivate(data.notes_private || "");
      setSummaryClient(data.summary_client || "");
    } catch (e) {
      toast.error("Could not load session");
    } finally {
      setLoading(false);
    }
  }, [bookingId]);
  useEffect(() => { load(); }, [load]);

  // Client-side session-ready hook: ack the notification + reload the booking so
  // sessionStarted flips true (auto-transitions the waiting room into the Daily frame).
  const { pendingSessionReady, ackSessionReady, pendingClientJoined, ackClientJoined } = useChat();
  useEffect(() => {
    if (!bookingId || !pendingSessionReady) return;
    const match = pendingSessionReady.find((n) => n.booking_id === bookingId);
    if (!match) return;
    ackSessionReady(bookingId);
    // Optimistically flip started_at so a client sitting in the waiting room moves straight
    // into the Daily frame. Also reload the booking so we pick up any other server updates.
    setBooking((b) => (b ? { ...b, started_at: match.started_at || b.started_at || new Date().toISOString() } : b));
    load();
    setTimeout(() => {
      const btn = document.querySelector('[data-testid="join-jitsi-btn"]');
      if (btn && typeof btn.scrollIntoView === "function") {
        btn.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 200);
  }, [bookingId, pendingSessionReady, ackSessionReady, load]);

  // Counsellor-side: ack the "client joined" notification whenever they open this session.
  useEffect(() => {
    if (!bookingId || !pendingClientJoined) return;
    const match = pendingClientJoined.find((n) => n.booking_id === bookingId);
    if (match) ackClientJoined(bookingId);
  }, [bookingId, pendingClientJoined, ackClientJoined]);

  // Tear down the Daily frame on unmount so it doesn't leak the media session
  useEffect(() => {
    return () => {
      if (dailyFrameRef.current) {
        try { dailyFrameRef.current.destroy(); } catch (e) { logger.debug("daily destroy failed (ignored):", e?.message); }
        dailyFrameRef.current = null;
      }
    };
  }, []);

  // Provision + join the Daily room when the user clicks "Join here"
  useEffect(() => {
    if (!joined) return;
    if (!dailyRef.current) return;
    if (dailyFrameRef.current) return;  // already mounted
    if (!booking || booking.session_mode !== "online") return;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post(`/bookings/${booking.booking_id}/video-token`);
        if (cancelled) return;
        const frame = DailyIframe.createFrame(dailyRef.current, {
          iframeStyle: { width: "100%", height: "100%", border: "0", borderRadius: "24px" },
          showLeaveButton: true,
          showFullscreenButton: true,
        });
        dailyFrameRef.current = frame;
        frame.on("left-meeting", () => {
          try { frame.destroy(); } catch (e) { logger.debug("daily destroy failed (ignored):", e?.message); }
          dailyFrameRef.current = null;
          setJoined(false);
        });
        // Daily emits an "error" event when the account/room state is unusable
        // (e.g. missing payment method) even after createFrame succeeds.
        frame.on("error", (evt) => {
          const rawMsg = (evt?.errorMsg || evt?.message || "").toLowerCase();
          logger.warn("Daily error event:", evt);
          let userMsg = "Video room could not be opened. Please try again.";
          if (rawMsg.includes("payment") || rawMsg.includes("billing")) {
            userMsg = "Video service is temporarily unavailable — the counselling team has been notified.";
          } else if (rawMsg.includes("room") && rawMsg.includes("not found")) {
            userMsg = "This session's video room has expired. Please refresh and try again.";
          } else if (rawMsg.includes("token")) {
            userMsg = "Your join pass expired. Please refresh the page and try again.";
          }
          toast.error(userMsg);
          try { frame.destroy(); } catch (e) { logger.debug("daily destroy failed (ignored):", e?.message); }
          dailyFrameRef.current = null;
          setJoined(false);
        });
        await frame.join({ url: data.url, token: data.token });
        if (data.url && data.url !== booking.video_room_url) {
          setBooking((b) => ({ ...b, video_room_url: data.url, video_provider: "daily" }));
        }
      } catch (err) {
        logger.warn("Daily join failed:", err);
        const msg = err?.response?.data?.detail || err?.message || "Video room could not be opened. Please try again.";
        toast.error(msg);
        setJoined(false);
      }
    })();
    return () => { cancelled = true; };
  }, [joined, booking?.booking_id, booking?.session_mode, booking?.video_room_url]);

  // Timer for the waiting room (declared BEFORE early return so hook order stays stable)
  useEffect(() => {
    if (!waitingForCounsellor) return undefined;
    const id = setInterval(() => setWaitingSecs((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [waitingForCounsellor]);

  // Auto-transition once the counsellor presses Start
  useEffect(() => {
    if (waitingForCounsellor && booking?.started_at) {
      setWaitingForCounsellor(false);
      setJoined(true);
    }
  }, [waitingForCounsellor, booking?.started_at]);

  // Client-only "I'm in the waiting room" heartbeat — 30s cadence.
  // Runs while the client is on this page, the session is online, hasn't started, and isn't
  // already in the video frame. Counsellor's dashboard converts recent pings into a
  // "Client waiting" pill so they can jump in early.
  useEffect(() => {
    if (user?.role !== "client" && user?.role !== "parent") return undefined;
    if (!booking || booking.session_mode !== "online") return undefined;
    if (booking.started_at || booking.status === "completed" || booking.status === "cancelled") return undefined;
    if (joined) return undefined;

    let cancelled = false;
    const ping = async () => {
      try { await api.post(`/bookings/${booking.booking_id}/waiting-ping`); }
      catch (err) { logger.debug("waiting ping failed (ignored):", err?.message); }
    };
    ping();
    const id = setInterval(() => { if (!cancelled) ping(); }, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user?.role, booking?.booking_id, booking?.session_mode, booking?.started_at, booking?.status, joined]);

  if (loading || !booking) return (
    <div className="min-h-screen"><Navbar /><div className="max-w-4xl mx-auto p-10 text-[#7A7D96]">Loading session…</div></div>
  );

  const other = user?.role === "counsellor" ? booking.client : booking.counsellor;

  const saveNotes = async () => {
    try {
      await api.patch(`/bookings/${bookingId}`, { notes_private: notesPrivate, summary_client: summaryClient });
      toast.success("Notes saved");
      load();
    } catch (e) { toast.error("Could not save"); }
  };

  const switchMode = async () => {
    if (switching) return;
    setSwitching(true);
    try {
      const { data } = await api.patch(`/bookings/${bookingId}/mode`);
      setBooking(data);
      toast.success(data.session_mode === "online" ? "Switched to online video" : "Switched to in-person");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not switch mode");
    } finally {
      setSwitching(false);
    }
  };

  const isCounsellor = user?.role === "counsellor";
  const isAdmin = user?.role === "admin";
  const targetMode = booking.session_mode === "online" ? "in_person" : "online";
  const switchLabel = targetMode === "online" ? "Switch to online video" : "Switch to in-person";

  // Client gate: can only join 10 min before start — unless counsellor has explicitly started it.
  const scheduledMs = new Date(booking.scheduled_at).getTime();
  const minsToStart = Math.round((scheduledMs - nowTick) / 60000);
  const sessionStarted = !!booking.started_at;
  const clientCanJoin = sessionStarted || minsToStart <= 10;
  const canJoin = isCounsellor || isAdmin || clientCanJoin;

  const startSession = async () => {
    if (starting || sessionStarted) { setJoined(true); return; }
    setStarting(true);
    try {
      await api.post(`/bookings/${booking.booking_id}/start`);
      setBooking((b) => ({ ...b, started_at: new Date().toISOString() }));
    } catch (err) {
      logger.warn("start session failed:", err?.message);
    } finally {
      setStarting(false);
      setJoined(true);
    }
  };

  // Client join button: if the counsellor hasn't pressed Start yet, drop the client
  // into the calm waiting room instead of hitting an empty Daily room.
  // Also POSTs /video-token in the background so the counsellor gets the "client here" ping.
  const clientJoin = async () => {
    if (sessionStarted) { setJoined(true); return; }
    setWaitingForCounsellor(true);
    setWaitingSecs(0);
    try {
      // Non-blocking: this fires the WS "client_joined" push to the counsellor.
      await api.post(`/bookings/${booking.booking_id}/video-token`);
    } catch (err) {
      // If the gate rejects (edge case: not yet in window), fall back to just showing the waiting room.
      logger.warn("client video-token pre-warm failed:", err?.response?.data?.detail || err?.message);
    }
  };

  // Auto-transition: once the counsellor presses Start, sessionStarted flips true
  // and we move the client from the waiting room into the Daily frame.
  // (Actual useEffect is declared above the early-return; this stub kept for readability.)

  const cancelWaiting = () => {
    setWaitingForCounsellor(false);
    setWaitingSecs(0);
  };

  const pingClient = async () => {
    if (pinging) return;
    setPinging(true);
    try {
      const { data } = await api.post(`/bookings/${booking.booking_id}/ping-client`);
      setLastPingedAt(data.pinged_at || new Date().toISOString());
      toast.success(`Ping sent to ${booking?.client?.name || "your client"} — they'll get a live notification + email`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not ping the client";
      if (e?.response?.status === 429) toast.info(detail);
      else toast.error(detail);
    } finally {
      setPinging(false);
    }
  };

  const completeSession = async () => {
    if (completing) return;
    if (!window.confirm("Mark this session as completed? This will end the video room and lock in the actual duration.")) return;
    setCompleting(true);
    try {
      const { data } = await api.post(`/bookings/${booking.booking_id}/complete`);
      setBooking((b) => ({ ...b, ended_at: data.ended_at, status: "completed" }));
      if (dailyFrameRef.current) {
        try { dailyFrameRef.current.leave(); } catch (e) { logger.debug("daily leave failed (ignored):", e?.message); }
      }
      toast.success(data.actual_minutes ? `Session completed (${data.actual_minutes} min)` : "Session completed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete session");
    } finally {
      setCompleting(false);
    }
  };

  const isEnded = !!booking.ended_at || booking.status === "completed";
  // Show Mark Completed once the counsellor has started (or session time has passed) and it's not already completed
  const showMarkCompleted = isCounsellor && !isEnded && (sessionStarted || minsToStart <= 0);

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
        <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm font-semibold text-[#7A7D96] hover:text-[#3D405B] mb-6" data-testid="session-back-btn">
          <ArrowLeft size={16} /> Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 brand-card p-8">
            <div className="overline mb-2">Session #{booking.booking_id.slice(-6)}</div>
            <h1 className="text-3xl font-extrabold text-[#3D405B] tracking-tight">{fmtFull(booking.scheduled_at)}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
              <span className="pill bg-secondary/15 text-secondary">{booking.session_type.replace("_", " ")}</span>
              <span className="pill bg-muted text-[#3D405B]">{booking.duration_minutes} min</span>
              <span className={`pill ${booking.session_mode === "in_person" ? "bg-accent/30 text-[#3D405B]" : "bg-primary/15 text-primary"}`} data-testid="session-mode-pill">
                {booking.session_mode === "in_person" ? "In person" : "Online"}
              </span>
              <span className={`pill ${booking.status === "completed" ? "bg-accent/30 text-[#3D405B]" : "bg-primary/15 text-primary"}`} data-testid="session-status-pill">{booking.status}</span>
              {booking.ended_at && (
                <span className="pill bg-muted text-[#7A7D96]" data-testid="session-ended-at">Ended {new Date(booking.ended_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</span>
              )}
            </div>

            {booking.topic && (
              <div className="mt-6 p-4 rounded-2xl bg-muted/50">
                <div className="text-xs font-bold uppercase tracking-wider text-[#7A7D96] mb-1">From the client</div>
                <p className="text-[#3D405B]">{booking.topic}</p>
              </div>
            )}

            {/* Flash banner: client has already joined but counsellor hasn't pressed Start yet */}
            {isCounsellor && booking.client_joined_at && !sessionStarted && !isEnded && (
              <div
                className="mt-6 p-5 rounded-2xl bg-secondary/10 border-2 border-secondary shadow-[0_0_0_6px_rgba(129,178,154,0.25)] animate-pulse flex flex-col sm:flex-row sm:items-center gap-4 justify-between"
                data-testid="client-joined-flash-banner"
              >
                <div className="flex items-start gap-3">
                  <span className="mt-1 w-3 h-3 rounded-full bg-secondary" />
                  <div>
                    <div className="text-lg font-extrabold text-[#3D405B]">
                      {booking.client?.name || "Your client"} is in the room 🌱
                    </div>
                    <div className="text-sm text-[#5F627D]">
                      They&apos;re waiting for you — press Start to bring them into the video.
                    </div>
                  </div>
                </div>
                <button
                  onClick={startSession}
                  disabled={starting}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-secondary text-white font-extrabold shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-60 whitespace-nowrap"
                  data-testid="flash-start-session-btn"
                >
                  <Video size={16} /> {starting ? "Starting…" : "Start now"}
                </button>
              </div>
            )}

            {/* Video room or in-person location */}
            {booking.session_mode === "in_person" ? (
              <div className="mt-8" data-testid="in-person-location-block">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <h2 className="text-xl font-extrabold text-[#3D405B] flex items-center gap-2"><MapPin size={20} className="text-primary" /> Where to meet</h2>
                  {isCounsellor && (
                    <button onClick={switchMode} disabled={switching} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-extrabold uppercase tracking-wider border border-[#3D405B]/15 text-[#3D405B] hover:bg-[#3D405B]/5 transition-all disabled:opacity-50" data-testid="switch-mode-btn">
                      <RefreshCcw size={13} /> {switching ? "Switching…" : switchLabel}
                    </button>
                  )}
                </div>
                <p className="text-sm text-[#7A7D96] mb-4">Please arrive a few minutes early. If you can&apos;t make it, cancel from your dashboard so the slot can free up.</p>
                <div className="p-5 rounded-2xl bg-accent/15 border border-accent/30">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#7A7D96] mb-1">Office address</div>
                  <div className="font-bold text-[#3D405B] text-lg">{booking.location || "Address not provided yet"}</div>
                  {booking.location_map_url && (
                    <a href={booking.location_map_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-primary hover:underline" data-testid="open-map-link">
                      Open in maps <ExternalLink size={13} />
                    </a>
                  )}
                </div>
                {isCounsellor && (
                  <p className="text-xs text-[#7A7D96] mt-3" data-testid="in-person-mode-note">No video room is created for in-person sessions. Use the switch above if circumstances change.</p>
                )}
                {showMarkCompleted && (
                  <button onClick={completeSession} disabled={completing} className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-secondary text-white font-bold shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-60" data-testid="mark-completed-btn">
                    <CheckCircle2 size={16} /> {completing ? "Marking…" : "Mark completed"}
                  </button>
                )}
              </div>
            ) : (
              <div className="mt-8">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <h2 className="text-xl font-extrabold text-[#3D405B] flex items-center gap-2"><Video size={20} className="text-primary" /> Private video room</h2>
                  {isCounsellor && (
                    <button onClick={switchMode} disabled={switching} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-extrabold uppercase tracking-wider border border-[#3D405B]/15 text-[#3D405B] hover:bg-[#3D405B]/5 transition-all disabled:opacity-50" data-testid="switch-mode-btn">
                      <RefreshCcw size={13} /> {switching ? "Switching…" : switchLabel}
                    </button>
                  )}
                </div>
                <p className="text-sm text-[#7A7D96] mb-4">When it&apos;s time, click join — your counsellor will meet you in the same room. No download required.</p>
                {(booking.video_room_url || booking.jitsi_room || booking.session_mode === "online") ? (
                  joined ? (
                    <div>
                      <div ref={dailyRef} className="rounded-3xl overflow-hidden border border-[#3D405B]/10" style={{ height: 560 }} data-testid="daily-embed" />
                    </div>
                  ) : waitingForCounsellor ? (
                    <WaitingRoom
                      counsellorName={booking.counsellor?.name}
                      secondsWaiting={waitingSecs}
                      onCancel={cancelWaiting}
                    />
                  ) : canJoin ? (
                    <div>
                      <PreCallCheck />
                      <div className="flex flex-wrap gap-3">
                        <button
                          onClick={isCounsellor ? startSession : (sessionStarted ? () => setJoined(true) : clientJoin)}
                          disabled={starting}
                          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-white font-bold shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-60"
                          data-testid="join-jitsi-btn"
                        >
                          <Video size={16} /> {starting ? "Starting…" : (isCounsellor && !sessionStarted ? "Start session" : "Join here")}
                        </button>
                        {isCounsellor && !sessionStarted && booking.session_mode === "online" && (
                          <button
                            onClick={pingClient}
                            disabled={pinging}
                            className="inline-flex items-center gap-2 px-5 py-3 rounded-full border-2 border-primary text-primary font-extrabold hover:bg-primary/5 transition-all disabled:opacity-60"
                            data-testid="ping-client-btn"
                            title="Send a live notification + email to your client to nudge them to join"
                          >
                            <Bell size={16} /> {pinging ? "Pinging…" : (lastPingedAt ? "Ping again" : "Ping client")}
                          </button>
                        )}
                        {isCounsellor && sessionStarted && (
                          <span className="pill bg-secondary/15 text-secondary self-center">● Session live</span>
                        )}
                        {showMarkCompleted && (
                          <button onClick={completeSession} disabled={completing} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-secondary text-white font-bold shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-60" data-testid="mark-completed-btn">
                            <CheckCircle2 size={16} /> {completing ? "Marking…" : "Mark completed"}
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="p-5 rounded-2xl bg-accent/15 border border-accent/40" data-testid="join-locked">
                      <div className="font-bold text-[#3D405B] mb-1 flex items-center gap-2">
                        <Clock size={16} className="text-secondary" /> Join opens 10 minutes before your session
                      </div>
                      <p className="text-sm text-[#5F627D]">
                        {minsToStart > 60 * 24
                          ? `Your session is in ${Math.round(minsToStart / 60 / 24)} day(s). Come back closer to the time.`
                          : minsToStart > 60
                            ? `Your session is in ${Math.round(minsToStart / 60)} hour(s). Come back closer to the time.`
                            : `Your session starts in ${Math.max(minsToStart, 0)} minute(s). The join button will unlock automatically at the 10-minute mark, or as soon as your counsellor starts the session.`}
                      </p>
                    </div>
                  )
                ) : (
                  <div className="p-4 rounded-2xl bg-muted/40 text-sm text-[#3D405B]" data-testid="no-room-yet">Video room not generated yet. {isCounsellor ? "Switch this session to in-person, or refresh — a new room will appear when this becomes online." : "Hang tight — your counsellor will set the room up shortly."}</div>
                )}
              </div>
            )}

            {/* Notes */}
            {user?.role === "counsellor" && (
              <div className="mt-8 border-t border-[#3D405B]/5 pt-7">
                <h2 className="text-xl font-extrabold text-[#3D405B] mb-3 flex items-center gap-2"><NotebookPen size={20} className="text-secondary" /> Session notes</h2>
                <label className="block text-xs font-bold text-[#3D405B] uppercase tracking-wider mb-2">Private notes (only you can see this)</label>
                <textarea rows={5} value={notesPrivate} onChange={(e) => setNotesPrivate(e.target.value)} className="w-full px-4 py-3 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none resize-none" data-testid="notes-private-textarea" />
                <label className="block text-xs font-bold text-[#3D405B] uppercase tracking-wider mb-2 mt-4">Summary for the client (they will see this)</label>
                <textarea rows={3} value={summaryClient} onChange={(e) => setSummaryClient(e.target.value)} className="w-full px-4 py-3 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none resize-none" data-testid="summary-client-textarea" />
                <button onClick={saveNotes} className="mt-4 px-6 py-2.5 rounded-full bg-secondary text-white font-bold hover:-translate-y-0.5 transition-all" data-testid="save-notes-btn">Save notes</button>
              </div>
            )}

            {(user?.role !== "counsellor") && booking.summary_client && (
              <div className="mt-8 border-t border-[#3D405B]/5 pt-7">
                <h2 className="text-xl font-extrabold text-[#3D405B] mb-3">Your session summary</h2>
                <div className="p-4 rounded-2xl bg-secondary/10 border border-secondary/20 text-[#3D405B]">{booking.summary_client}</div>
              </div>
            )}
          </div>

          <aside className="space-y-6">
            {isAdmin ? (
              <>
                <PersonCard label="Client" person={booking.client} testid="admin-view-client" showPresence presenceRole="client" />
                <PersonCard label="Counsellor" person={booking.counsellor} testid="admin-view-counsellor" showPresence />
              </>
            ) : (
              <PersonCard
                label={isCounsellor ? "Client" : "Counsellor"}
                person={other}
                testid="session-other-party"
                showPresence
                presenceRole={isCounsellor ? "client" : "counsellor"}
              />
            )}
            <div className="brand-card p-6">
              <ShieldCheck className="text-secondary mb-2" size={20} />
              <div className="font-bold text-[#3D405B]">100% confidential</div>
              <p className="text-sm text-[#7A7D96] mt-1">What you share in this session stays between you and your counsellor.</p>
            </div>
            <div className="brand-card p-6 bg-accent/20">
              <div className="font-bold text-[#3D405B] mb-1">Need help right now?</div>
              <p className="text-sm text-[#3D405B]">Call SADAG <strong>0800 567 567</strong> or Lifeline <strong>0861 322 322</strong>.</p>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
